import { FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { Input } from "../ui/input";
import StarIcon from "../../assets/svgs/common/star.svg";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { EyeIcon, EyeOff } from "lucide-react";
import React from "react";

type FormInputProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  className?: string;
  label?: string;
  placeholder: string;
  required?: boolean;
  type: string;
  min?: number;
  disabled?: boolean;
  onChange?: (value: string) => void;
  prefix?: string;
};

export const FormInput = <T extends FieldValues>({
  form,
  name,
  label,
  placeholder,
  required,
  className,
  type,
  disabled,
  min,
  prefix,
  onChange,
}: FormInputProps<T>) => {
  const [showPassword, setShowPassword] = React.useState<boolean>(false);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) =>
        type === "password" ? (
          <FormItem>
            <FormLabel className="font-semibold">Enter your password</FormLabel>
            <FormControl className="relative">
              <div className="flex items-center">
                <Input
                  type={showPassword ? "text" : "password"}
                  className={` py-6 pl-6 h-16 placeholder:text-secondary ${
                    showPassword
                      ? field.value.length > 0 && "tracking-wider"
                      : field.value.length > 0 && "tracking-5 "
                  }  `}
                  placeholder="Enter your password"
                  {...field}
                />
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                <div
                  onClick={() => setShowPassword(!showPassword)}
                  className="right-0 absolute mr-5 cursor-pointer"
                >
                  {showPassword ? <EyeOff /> : <EyeIcon />}
                </div>
              </div>
            </FormControl>
          </FormItem>
        ) : (
          <FormItem>
            <FormLabel className="flex items-center gap-1 font-semibold">
              {label} {required && <StarIcon />}
            </FormLabel>
            <FormControl>
              <div className="relative flex items-center">
				{prefix && (
                  <span className="left-0 z-10 absolute ml-5 font-semibold text-base">
                    {prefix}
                  </span>
                )}
                <Input
                  className={`py-6 pl-6 h-16 placeholder:text-secondary ${className} ${ 
                    prefix ? "pl-10" : ""
                  }`}
                  placeholder={placeholder}
                  disabled={disabled}
                  type={type}
                  min={min}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.(e.target.value);
                  }}
                />
              </div>
            </FormControl>
          </FormItem>
        )
      }
    />
  );
};
